import React, { useEffect, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import * as Yup from 'yup';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import BackIcon from '@assets/images/BackIcon.svg';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  CLINICIANS_ROUTE_PATH,
  VIEW_CLINICIANS_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomSelect from '@ui-components/Select/CustomSelect';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import CustomSwitch from '@ui-components/Switch/CustomSwitch';
import {
  useGetClinicianDetailsById,
  useGetClinicianMetaData,
  useSaveOrUpdateClinician,
} from '../../API/hooks/ClinicianHook';
import type {
  BindStringArray,
  ClinicianRequest,
} from '../../API/services/ClinicianService';
import { Form, Formik } from 'formik';
import formatPhoneNumber from '../components/FormatPhoneNumber';
import validatePostalCode from '../components/ValidatePostalCode';
import CustomButton from '@ui-components/Button/CustomButton';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import ClinicianInitialData from './ClinicianInitialData';

interface NewClinicianRouteState {
  firstName?: string;
  lastName?: string;
  email?: string;
  isClinicianEdit?: boolean;
}

const AddNewClinician: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') ?? '0';
  const { clinicianId } = useParams<{ clinicianId?: string }>();
  const location = useLocation() as {
    state?: NewClinicianRouteState;
  };
  const { firstName, lastName, email } = location.state || {};
  const [clinicianSubmitAction, setClinicianSubmitAction] =
    useState<string>('');
  const [openClinicianConfirmDialog, setOpenClinicianConfirmDialog] =
    useState(false);
  const [initialClinicianValues, setInitialClinicianValues] =
    useState<ClinicianRequest>({
      ...ClinicianInitialData, // keep full base structure
      file: null,

      clinicianRequest: {
        ...ClinicianInitialData.clinicianRequest,

        userInfo: {
          ...ClinicianInitialData.clinicianRequest.userInfo,
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          email: email ?? '',
          // title: 'DR',
        },
      },
    });

  const { clinicianMetaData } = useGetClinicianMetaData(userId);

  const onSuccess = () => {
    if (clinicianId) {
      navigate(`${CLINICIANS_ROUTE_PATH}?toast=update`);
    } else {
      navigate(`${CLINICIANS_ROUTE_PATH}?toast=success`);
    }
  };

  const { mutate: saveOrUpdateClinician } = useSaveOrUpdateClinician(onSuccess);

  const handleClinicianSaveOrUpdate = (values: ClinicianRequest) => {
    const { ...payload } = values.clinicianRequest;

    saveOrUpdateClinician({
      userId,
      action: clinicianSubmitAction,
      ...payload,
    });
  };

  const handleOpenClinicianConfirmDialog = (state: boolean) => {
    setOpenClinicianConfirmDialog(state);
  };

  const mapddlWithGivenOptions = (
    items: string[] | undefined
  ): BindStringArray[] => {
    return (
      items?.map((item) => ({
        name: item,
        id: item,
      })) ?? []
    );
  };

  const { ClinicianDetailsById, isLoading } = useGetClinicianDetailsById(
    userId,
    Number(clinicianId)
  );

  useEffect(() => {
    if (!ClinicianDetailsById) return;

    setInitialClinicianValues({
      ...ClinicianInitialData,
      file: null,

      clinicianRequest: {
        ...ClinicianInitialData.clinicianRequest,

        userInfo: {
          ...ClinicianInitialData.clinicianRequest.userInfo,
          ...ClinicianDetailsById.clinicianRequest.userInfo,
        },

        providerDetails: {
          ...ClinicianInitialData.clinicianRequest.providerDetails,
          ...ClinicianDetailsById.clinicianRequest.providerDetails,
        },

        address: {
          ...ClinicianInitialData.clinicianRequest.address,
          ...ClinicianDetailsById.clinicianRequest.address,
        },
      },
    });
  }, [ClinicianDetailsById]);

  const validateTenNumbers = (value: string) => {
    if (!value) return value;

    // Remove any non-digit characters
    const digitsOnly = value.replaceAll(/\D/g, '');

    // Limit to 5 digits
    return digitsOnly.slice(0, 10);
  };

  const validationSchema = Yup.object().shape({
    clinicianRequest: Yup.object().shape({
      userInfo: Yup.object().shape({
        firstName: Yup.string().required('First Name is required'),
        lastName: Yup.string().required('Last Name is required'),
        categoryId: Yup.number()
          .required('Category is required')
          .min(1, 'Category is required'),
        email: Yup.string()
          .email('Invalid email address')
          .required('Email is required'),
        alternateEmail: Yup.string().email('Invalid email address'),
        maxConcurrentConsults: Yup.number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .required('Max Concurrent Consults is required')
          .max(10, 'Max Concurrent Consults cannot be greater than 10'),
        phone: Yup.string()
          .required('Primary Phone is required')
          .test(
            'valid-phone',
            'Primary Phone must be in format (123) 456-7890',
            (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
          ),
        alternatePhone: Yup.string()
          .nullable()
          .test(
            'valid-phone',
            'Alternate Phone must be in format (123) 456-7890',
            (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
          ),

        gender: Yup.string()
          .transform((v) => (v ? v.trim() : ''))
          .required('Gender is required')
          .test(
            'not-default',
            'Please select a Gender',
            (value) => value !== '' && value !== 'Select' && value !== '0'
          ),
      }),

      address: Yup.object().shape({
        postalCode: Yup.string()
          .transform((value) => (value === '0' || value === 0 ? null : value))
          .nullable()
          .test(
            'valid-postalCode',
            'Postal Code must be exactly 5 digits',
            (value) => !value || /^\d{5}$/.test(value)
          ),
      }),
    }),
  });

  const mapGender = (gender: string[]) =>
    gender.map((t) => ({
      name: t,
      id: t.replace('.', '').toUpperCase(),
    }));

  const handleClinicianBackButtonClick = () => {
    if (clinicianId && !location.state?.isClinicianEdit) {
      navigate(VIEW_CLINICIANS_ROUTE_PATH + `/${clinicianId}`, {
        replace: true,
      });
    } else {
      navigate(CLINICIANS_ROUTE_PATH);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  handleClinicianBackButtonClick();
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-facility-text"
              >
                {clinicianId ? 'Edit Clinician' : 'Add New Clinician'}
              </CustomTypography>
              <CustomTypography
                className="pl-12"
                variant="h4"
                id="partner-business-id"
              >
                {clinicianId
                  ? `(${initialClinicianValues.clinicianRequest.userInfo.userBusinessId ?? ''})`
                  : ''}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        initialValues={initialClinicianValues}
        validateOnMount={false}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          console.log('Form submitted:', values);
          handleClinicianSaveOrUpdate(values);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Box className="form-background p-24">
              <Grid2 container>
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-32"
                    id="user-info-text"
                  >
                    User Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Box className="display-flex">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-first-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      First Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinician-first-name"
                      name="firstName"
                      placeholder="Type First Name"
                      size="small"
                      value={values.clinicianRequest.userInfo.firstName}
                      disabled={Boolean(
                        firstName ?? values.clinicianRequest.userInfo.firstName
                      )}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.firstName',
                          event.target.value
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.userInfo?.firstName &&
                      touched?.clinicianRequest?.userInfo?.firstName && (
                        <CustomTypography
                          id="validate-first-name"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.firstName}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-last-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Last Name&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinician-last-name"
                      name="lastName"
                      placeholder="Type Last Name"
                      size="small"
                      disabled={Boolean(
                        lastName ?? values.clinicianRequest.userInfo.lastName
                      )}
                      value={values.clinicianRequest.userInfo.lastName}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.lastName',
                          event.target.value
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.userInfo?.lastName &&
                      touched?.clinicianRequest?.userInfo?.lastName && (
                        <CustomTypography
                          id="validate-last-name"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.lastName}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-status-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Status
                    </CustomTypography>
                    <Box className="text-align-left">
                      <CustomSwitch
                        id="clinician-status-switch"
                        checked={values.clinicianRequest.userInfo.status}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.userInfo.status',
                            event.target.checked
                          );
                        }}
                        labelPlacement="end"
                        label={
                          values.clinicianRequest.userInfo.status
                            ? 'Active'
                            : 'Inactive'
                        }
                      />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="preferred-name-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Preferred Name
                    </CustomTypography>
                    <CustomTextField
                      id="txt-preferred-name"
                      name="preferredName"
                      placeholder="Type Preferred Name"
                      size="small"
                      value={values.clinicianRequest.userInfo.preferredName}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.preferredName',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="suffix-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Suffix
                    </CustomTypography>
                    <CustomTextField
                      id="txt-suffix"
                      name="suffix"
                      placeholder="Type Suffix"
                      size="small"
                      value={values.clinicianRequest.userInfo.suffix}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.suffix',
                          event.target.value
                        );
                      }}
                    ></CustomTextField>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="category-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Category&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomSelect
                      id="category-select"
                      placeholder="Select Category"
                      size="small"
                      options={clinicianMetaData?.clinicianCategories ?? []}
                      onChange={(value) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.categoryId',
                          Number(value)
                        );
                      }}
                      selectedValue={
                        values.clinicianRequest.userInfo.categoryId ?? 0
                      }
                      fullWidth
                    />
                    {errors?.clinicianRequest?.userInfo?.categoryId &&
                      touched?.clinicianRequest?.userInfo?.categoryId && (
                        <CustomTypography
                          id="validate-category-id"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.categoryId}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="max-concurrent-consults-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Max Concurrent Consults{' '}
                      <span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-max-concurrent-consults"
                      name="maxConcurrentConsults"
                      placeholder="Type Max Concurrent Consults"
                      size="small"
                      type="number"
                      value={
                        values.clinicianRequest.userInfo.maxConcurrentConsults
                      }
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.maxConcurrentConsults',
                          event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.userInfo
                      ?.maxConcurrentConsults &&
                      touched?.clinicianRequest?.userInfo
                        ?.maxConcurrentConsults && (
                        <CustomTypography
                          id="validate-max-concurrent-consults"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {
                            errors.clinicianRequest.userInfo
                              .maxConcurrentConsults
                          }
                        </CustomTypography>
                      )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box className="pt-32">
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="provider-info-text"
                    >
                      Provider Info
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Grid2 container spacing={4} className="width-80">
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Email&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinician-email"
                      name="lastName"
                      placeholder="Type Email"
                      size="small"
                      value={values.clinicianRequest.userInfo.email}
                      disabled={Boolean(
                        email ?? values.clinicianRequest.userInfo.email
                      )}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.email',
                          event.target.value
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-primary-phone-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Primary Phone&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinician-primary-phone"
                      placeholder="Type Primary Phone"
                      size="small"
                      value={values.clinicianRequest.userInfo.phone}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'clinicianRequest.userInfo.phone',
                          formatted
                        );
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'clinicianRequest.userInfo.phone',
                          formatted
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.userInfo?.phone &&
                      touched?.clinicianRequest?.userInfo?.phone && (
                        <CustomTypography
                          id="validate-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.phone}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-npi-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      NPI
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinical-npi"
                      placeholder="Type NPI Number"
                      size="small"
                      type="number"
                      value={values.clinicianRequest.providerDetails.npi}
                      onChange={(event) => {
                        const formatted = validateTenNumbers(
                          event.target.value
                        );
                        setFieldValue(
                          'clinicianRequest.providerDetails.npi',
                          formatted
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.providerDetails?.npi &&
                      touched?.clinicianRequest?.providerDetails?.npi && (
                        <CustomTypography
                          id="validate-ed-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.providerDetails.npi}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="alternate-email-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Alternate Email
                    </CustomTypography>
                    <CustomTextField
                      id="txt-alternate-email"
                      placeholder="Type Alternate Email"
                      size="small"
                      value={values.clinicianRequest.userInfo.alternateEmail}
                      onChange={(event) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.alternateEmail',
                          event.target.value
                        );
                      }}
                      onBlur={handleBlur}
                    />
                    {errors?.clinicianRequest?.userInfo?.alternateEmail &&
                      touched?.clinicianRequest?.userInfo?.alternateEmail && (
                        <CustomTypography
                          id="validate-alternate-email"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.alternateEmail}
                        </CustomTypography>
                      )}
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-alternate-phone-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Alternate Phone
                    </CustomTypography>
                    <CustomTextField
                      id="txt-clinician-alternate-phone"
                      placeholder="Type Alternate Phone"
                      size="small"
                      value={values.clinicianRequest.userInfo.alternatePhone}
                      onChange={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'clinicianRequest.userInfo.alternatePhone',
                          formatted
                        );
                      }}
                      onBlur={(event) => {
                        const formatted = formatPhoneNumber(event.target.value);
                        setFieldValue(
                          'clinicianRequest.userInfo.alternatePhone',
                          formatted
                        );
                      }}
                    />
                    {errors?.clinicianRequest?.userInfo?.alternatePhone &&
                      touched?.clinicianRequest?.userInfo?.alternatePhone && (
                        <CustomTypography
                          id="validate-ed-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.alternatePhone}
                        </CustomTypography>
                      )}
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                    <CustomTypography
                      id="clinician-gender-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Gender&nbsp;<span className="color-red">*</span>
                    </CustomTypography>
                    <CustomSelect
                      id="clinician-gender-select"
                      placeholder="Select Gender"
                      size="small"
                      selectedValue={values.clinicianRequest.userInfo.gender}
                      onChange={(value) => {
                        setFieldValue(
                          'clinicianRequest.userInfo.gender',
                          value
                        );
                      }}
                      options={mapGender(clinicianMetaData?.genders ?? [])}
                      fullWidth
                    />
                    {errors?.clinicianRequest?.userInfo?.gender &&
                      touched?.clinicianRequest?.userInfo?.gender && (
                        <CustomTypography
                          id="validate-gender"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.clinicianRequest.userInfo.gender}
                        </CustomTypography>
                      )}
                  </Grid2>
                </Grid2>
              </Box>
              <Box className="pt-32">
                <CustomDivider />
              </Box>
              <Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="clinician-address-details-text"
                      >
                        Address Details
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-address-line-1-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Address Line 1
                      </CustomTypography>
                      <CustomTextField
                        id="txt-clinician-mailing-address-line-1"
                        placeholder="Type Address Line 1"
                        size="small"
                        value={values.clinicianRequest.address.addressLine1}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.address.addressLine1',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-city-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        City
                      </CustomTypography>
                      <CustomTextField
                        id="txt-clinician-mailing-city"
                        placeholder="Type City"
                        size="small"
                        value={values.clinicianRequest.address.city}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.address.city',
                            event.target.value
                          );
                        }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-postal-code-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Postal Code
                      </CustomTypography>
                      <CustomTextField
                        id="txt-clinician-mailing-postal-code"
                        placeholder="Type  Postal Code"
                        size="small"
                        type="number"
                        value={
                          Number(values.clinicianRequest.address.postalCode) ===
                          0
                            ? ''
                            : values.clinicianRequest.address.postalCode
                        }
                        onChange={(event) => {
                          const formatted = validatePostalCode(
                            event.target.value
                          );
                          setFieldValue(
                            'clinicianRequest.address.postalCode',
                            formatted
                          );
                        }}
                      />
                      {errors.clinicianRequest?.address?.postalCode &&
                        touched.clinicianRequest?.address?.postalCode && (
                          <CustomTypography
                            id="clinician-mailing-postal-code-error"
                            variant="body3"
                            color="error"
                            className="text-align-left"
                          >
                            {errors.clinicianRequest.address?.postalCode}
                          </CustomTypography>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-address-line-2-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Address Line 2
                      </CustomTypography>
                      <CustomTextField
                        id="txt-clinician-mailing-address-line-2"
                        placeholder="Type Address Line 2"
                        size="small"
                        value={values.clinicianRequest.address.addressLine2}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.address.addressLine2',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-state-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        State
                      </CustomTypography>
                      <CustomSelect
                        id="select-clinician-mailing-state"
                        placeholder="Select State"
                        size="small"
                        onChange={(value) => {
                          setFieldValue(
                            'clinicianRequest.address.state',
                            value
                          );
                        }}
                        selectedValue={values.clinicianRequest.address.state}
                        options={mapddlWithGivenOptions(
                          clinicianMetaData?.usStates ?? []
                        )}
                        fullWidth
                      ></CustomSelect>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-mailing-county-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        County
                      </CustomTypography>
                      <CustomTextField
                        id="txt-clinician-mailing-county"
                        placeholder="Type County"
                        size="small"
                        value={values.clinicianRequest.address.county}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.address.county',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="provider-notes-text"
                      >
                        Provider Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                      <CustomTypography
                        id="provider-notes-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Notes
                      </CustomTypography>
                      <CustomTextField
                        id="txt-provider-notes"
                        placeholder="Type Notes"
                        size="small"
                        multiLine
                        rows={4}
                        value={values.clinicianRequest.userInfo.notes}
                        onChange={(event) => {
                          setFieldValue(
                            'clinicianRequest.userInfo.notes',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                      {errors.clinicianRequest?.userInfo?.notes &&
                        touched?.clinicianRequest?.userInfo?.notes && (
                          <CustomTypography
                            id="validate-provider-notes"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.clinicianRequest.userInfo.notes}
                          </CustomTypography>
                        )}
                    </Grid2>
                  </Grid2>
                </Box>

                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="clinician-zoom-details-text"
                      >
                        Zoom Details
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="clinician-zoom-user-id-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Zoom User ID
                      </CustomTypography>
                      <CustomTypography
                        id="zoom-user-id"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        {values.clinicianRequest.providerDetails.zoomUserId}
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                </Box>
              </Box>
              <Box className="pt-32">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <Box className="display-flex justify-end">
                      <Box className="pr-12">
                        <CustomButton
                          id="cancel-button"
                          onClick={() => {
                            handleOpenClinicianConfirmDialog(true);
                          }}
                          variant="outlined"
                        >
                          Cancel
                        </CustomButton>
                      </Box>
                      <Box>
                        <CustomButton
                          id="save-button"
                          onClick={() => {
                            setClinicianSubmitAction(
                              clinicianId ? 'Update' : 'Save'
                            );
                            handleSubmit();
                          }}
                          variant="contained"
                        >
                          {clinicianId ? 'Update' : 'Save'}
                        </CustomButton>
                      </Box>
                    </Box>
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
      <Box>
        <Box>
          <ConfirmDiscardDialog
            open={openClinicianConfirmDialog}
            onClose={() => handleOpenClinicianConfirmDialog(false)}
            navigateTo={
              clinicianId && !location.state?.isClinicianEdit
                ? `${VIEW_CLINICIANS_ROUTE_PATH}/${clinicianId}`
                : CLINICIANS_ROUTE_PATH
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AddNewClinician;
